import React from 'react';
import { Column } from 'react-table';

import { RadioInput } from 'app/components/Input/RadioInput';
import StatusDefault from 'app/components/Status/StatusDefault';
import { ActionsCell } from 'app/components/Table/columns/ActionsCell';
import { CustomHeader } from 'app/components/Table/columns/CustomHeader';
import { ActionTable, ID, TYPE_MODAL } from 'app/components/Table/core/types';
import { TippyDefault } from 'app/components/Tippy';
import Typography, { EmptyTableCell } from 'app/components/Typography';

import { useAuth } from 'app/modules/auth';
import { SensorStatus } from 'app/modules/inventory/bins/components/SensorStatus';
import { SensorConnectionStatus, SensorSkuId } from 'app/modules/inventory/bins/core/enum';
import ExpiryContent from 'app/modules/inventory/racks/components/ExpiryContent';
import { HIGHLIGHT_EXPIRY_STATUS } from 'app/modules/inventory/racks/core/constants';
import { StockLevel } from 'app/modules/inventory/stock-run/core/enum';
import { getStockHealth } from 'app/modules/reporting/core/columns';

import {
  coverPath,
  formatCustomDate,
  formatDate,
  getLabelAction,
  getUser,
  isActiveLabelAction,
} from 'app/helpers';
import { representativeExpiryStatus } from 'app/helpers/utils';
import { usePolicyInUser } from 'app/hooks/policy';
import { capitalize, compact, isEmpty } from 'lodash';

import { PATH } from 'app/constants/path';

import { Bin } from '../../bins/core/type';
import ItemInfoCard from '../../items/components/ItemInfoCard';
import SKUCard from '../../items/components/SKUCard';
import { UserInfo } from '../../items/components/UserInfo';
import StatusStockRunCard from '../../stock-run/components/StatusStockRunCard';
import { StockRun } from '../../stock-run/core/type';
import ActiveTabletCard from '../components/ActiveTabletCard';
import { QtyTypography } from '../components/Typography';
import '../styles/index.scss';
import { InventoryMovementType } from './enum';
import {
  ActivityLogRack,
  HistoryRack,
  IBinsAlreadyAssignedEntity,
  LogItemDetail,
  Rack,
} from './type';

export const racksColumns = (
  onOpenUnableRemoveRackModal: (rack: Rack) => () => void,
): ReadonlyArray<Column<Rack>> => {
  const actions = (data: Rack) => {
    const listActions: ActionTable[] = [
      {
        name: 'Edit',
        type: TYPE_MODAL.EDIT,
        titleModal: 'Edit rack',
      },
    ];
    /* id not delete */
    if (data.id !== 1 && data.id !== 2 && data.id !== 3) {
      data.isAllowedToDelete
        ? listActions.push({
            name: 'Delete',
            type: TYPE_MODAL.DELETE,
            titleModal: 'Delete rack',
          })
        : listActions.push({
            name: 'Delete',
            type: TYPE_MODAL.NONE,
            onClick: onOpenUnableRemoveRackModal(data),
          });
    }
    return listActions;
  };

  return [
    {
      id: 'name',
      Header: (props) => <CustomHeader tableProps={props} title="Rack" />,
      Cell: ({ ...props }) => (
        <div className="d-flex flex-column">
          <Typography lineHeight={26} variant="body-1">
            {props.data[props.row.index].name}
          </Typography>
        </div>
      ),
    },
    {
      id: 'activeTablet',
      width: '14%',
      Cell: ({ ...props }) => (
        <ActiveTabletCard
          wardId={parseInt(props.data[props.row.index].location?.id?.toString() || '0')}
        />
      ),
    },
    // {
    //   id: 'connectionStatus',
    //   Header: 'Raspberry Pi connection',
    //   width: '15%',
    //   Cell: ({ ...props }) => (
    //     <HeartBeatStatus pageType="rack" targetId={Number(props.data[props.row.index].id)} />
    //   ),
    // },
    {
      id: 'isCustom',
      Header: 'Type',
      width: '15%',
      Cell: ({ ...props }) => {
        const isTypeCustom = props.data[props.row.index]['is_custom'];
        return (
          <div
            style={{
              borderRadius: '4px',
              background: `${isTypeCustom ? 'var(--neutral-3)' : 'var(--success-light-1-5)'}`,
              width: '90px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '22px',
                color: isTypeCustom ? 'var(--neutral-7)' : 'var(--success)',
              }}
            >
              {isTypeCustom ? 'Non-smart' : 'Smart'}
            </div>
          </div>
        );
      },
    },
    {
      id: 'bin',
      Header: 'Number of Bins',
      width: '15%',
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {props.data[props.row.index].bins}
        </Typography>
      ),
    },
    {
      id: 'ward',
      width: '15%',
      Header: (props) => <CustomHeader tableProps={props} title="Location" />,
      Cell: ({ ...props }) =>
        props.data[props.row.index].location ? (
          <Typography lineHeight={26} variant="body-1">
            {props.data[props.row.index].location?.name || ''}
          </Typography>
        ) : (
          EmptyTableCell
        ),
    },
    {
      id: 'createdAt',
      width: '15%',
      Header: (props) => <CustomHeader tableProps={props} title="Created at" />,
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {formatCustomDate(props.data[props.row.index]['created_at'])}
        </Typography>
      ),
    },
    {
      id: 'actions',
      width: '5%',
      Header: (props) => (
        <CustomHeader tableProps={props} title="" className="text-end min-w-50px" />
      ),
      Cell: ({ ...props }) => (
        <ActionsCell
          item={props.data[props.row.index]}
          actions={actions(props.data[props.row.index])}
        />
      ),
    },
  ];
};

const getActions = (
  data: Bin,
  wardId: ID,
  onDownloadBin: (binId: ID) => () => void,
  onDownloadBinLedger: (binId: ID) => () => void,
): ActionTable[] => {
  const currentUser = useAuth().currentUser;
  const isSystemAdminCurrent = usePolicyInUser(currentUser?.policy ?? []).systemAdmin;

  return compact([
    data.item && {
      name: 'Quantity Adjustment',
      type: TYPE_MODAL.EDIT_QUANTITY,
    },
    data.sensor?.skuId === SensorSkuId.Weight && {
      name: 'Update Base Weight',
      type: TYPE_MODAL.UPDATE_BASE_WEIGHT,
    },
    data.item
      ? {
          name: 'Remove item',
          type: TYPE_MODAL.REMOVE_ITEM,
          titleModal: 'Remove item',
        }
      : {
          name: 'Assign item',
          type: TYPE_MODAL.ASSIGN_ITEM,
          titleModal: 'Assign item',
        },
    data.sensor
      ? {
          name: 'Remove sensor',
          type: TYPE_MODAL.REMOVE_SENSOR,
          titleModal: 'Remove sensor',
        }
      : {
          name: 'Assign sensor',
          type: TYPE_MODAL.ASSIGN_SENSOR,
          titleModal: 'Assign sensor',
        },
    data.expirySensor
      ? {
          name: 'Remove expired sensor',
          type: TYPE_MODAL.REMOVE_SENSOR_EXPIRED,
          titleModal: 'Remove expired sensor',
        }
      : {
          name: 'Assign expired sensor',
          type: TYPE_MODAL.ASSIGN_SENSOR_EXPIRED,
          titleModal: 'Assign expired sensor',
        },
    data.item && {
      name: 'Add to a stock run',
      type: TYPE_MODAL.SHORT_FOR,
    },
    {
      name: 'Show bin history',
      type: TYPE_MODAL.EDIT,
      redirect: PATH.BIN_HISTORY,
    },
    isSystemAdminCurrent && {
      name: 'Export MQTT',
      type: TYPE_MODAL.NONE,
      onClick: onDownloadBin(data.id),
    },
    isSystemAdminCurrent && {
      name: 'Export Ledger',
      type: TYPE_MODAL.NONE,
      onClick: onDownloadBinLedger(data.id),
    },
    data.item && {
      name: 'Go to item report',
      type: TYPE_MODAL.EDIT,
      redirect: coverPath(PATH.REPORT_DETAIL, {
        wardId,
        itemId: data.item.id,
      }),
    },
  ]);
};

export const binInRackColumns = (
  inSession: boolean,
  onClickNameAndSKU: (itemId: string) => void,
  wardId: ID,
  onDownloadBin: (binId: ID) => () => void,
  onDownloadBinLedger: (binId: ID) => () => void,
): ReadonlyArray<Column<Bin>> => {
  return [
    {
      id: 'name',
      width: '10%',
      Header: (props) => <CustomHeader tableProps={props} title="Bin" />,
      Cell: ({ ...props }) => (
        <div className="d-flex flex-column">
          <Typography variant="body-6" color="neutral-10">
            {props.data[props.row.index].name}
          </Typography>
        </div>
      ),
    },
    {
      id: 'expiry',
      width: '5%',
      Header: (props) => <CustomHeader tableProps={props} title="Expiry status" />,
      Cell: ({ ...props }) => {
        const expiry = props.data[props.row.index].expiry;

        if (
          isEmpty(expiry) ||
          !expiry.some((item) => item.expiryDate) ||
          expiry.every((item) => item.qty === 0)
        )
          return <></>;

        const highlightedExpiryStatus = HIGHLIGHT_EXPIRY_STATUS[representativeExpiryStatus(expiry)];

        return (
          <TippyDefault containerClass="mb-1" content={<ExpiryContent expiries={expiry} />}>
            <Typography
              variant="body-8"
              className="mt-1"
              style={{ color: highlightedExpiryStatus.color, whiteSpace: 'nowrap' }}
            >
              {highlightedExpiryStatus.text}
            </Typography>
          </TippyDefault>
        );
      },
    },
    {
      id: 'item.name',
      Header: (props) => <CustomHeader tableProps={props} title="Item" />,
      Cell: ({ ...props }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          {props.data[props.row.index].item ? (
            <TippyDefault arrow={true} theme="dark" content={getTooltipTitle()}>
              <div
                onClick={() =>
                  onClickNameAndSKU(props.data[props.row.index].item?.id?.toString() || '')
                }
              >
                <ItemInfoCard
                  image={props.data[props.row.index].item?.image || ''}
                  name={props.data[props.row.index].item?.name || ''}
                  stockType={props.data[props.row.index].item?.stockType}
                />
              </div>
            </TippyDefault>
          ) : (
            <Typography
              lineHeight={26}
              style={{ height: '56px', display: 'flex', alignItems: 'center' }}
            >
              {'No item assigned'}
            </Typography>
          )}
          {props.data[props.row.index].inSession && (
            <div
              style={{
                width: 81,
                height: 28,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--primary-3)',
              }}
            >
              <Typography fontSize={13} fontWeight={600} color="primary">
                In session
              </Typography>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'sensor.code',
      Header: (props) => <CustomHeader tableProps={props} title="Assigned Sensor" />,
      Cell: ({ ...props }) => {
        const sensor = props.data[props.row.index]?.sensor?.skuId;

        return (
          <div className="d-flex align-items-center gap-4">
            {sensor ? (
              <SensorStatus
                status={
                  props.data[props.row.index]?.sensor?.heartbeatStatus ??
                  SensorConnectionStatus.notReady
                }
                skuId={sensor}
              />
            ) : null}
            <Typography variant="body-1" lineHeight={26}>
              {props.data[props.row.index]?.sensor?.code || ''}
            </Typography>
          </div>
        );
      },
    },
    {
      id: 'expirySensor.name',
      accessor: 'expirySensor',
      Header: (props) => <CustomHeader tableProps={props} title="Assigned Expiry Sensor" />,
      Cell: ({ value, ...props }) =>
        !value ? (
          <></>
        ) : (
          <div className="d-flex align-items-center gap-4">
            <SensorStatus
              status={
                props.data[props.row.index]?.expirySensor?.heartbeatStatus ??
                SensorConnectionStatus.notReady
              }
              skuId={SensorSkuId.Expiry}
            />
            <Typography variant="body-1" lineHeight={26}>
              {value.name}
            </Typography>
          </div>
        ),
    },
    {
      id: 'item.skuId',
      Header: (props) => <CustomHeader tableProps={props} title="SKU" />,
      width: '20%',
      Cell: ({ ...props }) => (
        <div className="d-flex flex-column">
          {props.data[props.row.index].item ? (
            <div
              onClick={() =>
                onClickNameAndSKU(props.data[props.row.index].item?.id?.toString() || '')
              }
              style={{
                cursor: 'pointer',
                width: 'fit-content',
              }}
            >
              <TippyDefault arrow={true} theme="dark" content={getTooltipTitle()}>
                <SKUCard
                  skuId={props.data[props.row.index].item?.skuId || ''}
                  skuSub={props.data[props.row.index].item?.skuSub || ''}
                  containerStyle={{ width: 'fit-content' }}
                />
              </TippyDefault>
            </div>
          ) : (
            EmptyTableCell
          )}
        </div>
      ),
    },
    {
      id: 'item.qtyUnit.name',
      Header: (props) => <CustomHeader tableProps={props} title="Unit" />,
      Cell: ({ row }) => (
        <Typography variant="body-5">{capitalize(row.original.item?.qtyUnit?.name)}</Typography>
      ),
    },
    {
      id: 'item.qty',
      width: '10%',
      Header: (props) => <CustomHeader tableProps={props} title="Qty" />,
      Cell: ({ ...props }) => (
        <div className="d-flex flex-column">
          {props.data[props.row.index].item ? (
            <Typography
              lineHeight={26}
              variant="body-1"
              className="text-overflow-ellipsis number-width-5"
            >
              {props.data[props.row.index].item?.qty || '0'}
            </Typography>
          ) : (
            EmptyTableCell
          )}
        </div>
      ),
    },
    {
      id: 'item.stockLevelOrder',
      width: '10%',
      Header: (props) => <CustomHeader tableProps={props} title="Location Health" />,
      Cell: ({ ...props }) =>
        getStockHealth(props.data[props.row.index].item?.stockLevel ?? StockLevel.normal),
    },
    {
      id: 'actions',
      width: '5%',
      Header: (props) => (
        <CustomHeader tableProps={props} title="" className="text-end min-w-100px" />
      ),
      Cell: ({ ...props }) => {
        const actions = getActions(
          props.data[props.row.index],
          wardId,
          onDownloadBin,
          onDownloadBinLedger,
        );
        return (
          <ActionsCell
            item={props.data[props.row.index]}
            actions={actions}
            inSession={inSession}
            dividerIndexes={[actions.length - 4]}
          />
        );
      },
    },
  ];
};

export const historyRackColumns = (): ReadonlyArray<Column<HistoryRack>> => {
  return [
    {
      id: 'expriryDate',
      accessor: 'expriryDate',
      width: '10%',
      Header: (props) => <CustomHeader tableProps={props} title="Date modified" />,
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {formatCustomDate(props.data[props.row.index].expriryDate)}
        </Typography>
      ),
    },
    {
      id: 'bin',
      accessor: 'bin',
      width: '10%',
      Header: (props) => <CustomHeader tableProps={props} title="Bin" />,
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {props.data[props.row.index].bin?.name}
        </Typography>
      ),
    },
    {
      id: 'item',
      accessor: 'inventory',
      Header: (props) => <CustomHeader tableProps={props} title="Item" />,
      Cell: ({ ...props }) => (
        <ItemInfoCard
          image={props.data[props.row.index].inventory?.image || ''}
          name={props.data[props.row.index].inventory?.name || ''}
          stockType={props.data[props.row.index].inventory?.stockType}
          isNotNavigatable={true}
        />
      ),
    },
    {
      id: 'sku',
      Header: (props) => <CustomHeader tableProps={props} title="SKU" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <SKUCard
          skuId={props.data[props.row.index].inventory?.skuId || ''}
          skuSub={props.data[props.row.index].inventory?.skuSub || ''}
        />
      ),
    },
    {
      id: 'type',
      accessor: 'type',
      Header: (props) => <CustomHeader tableProps={props} title="Action" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <StatusDefault
          isActive={isActiveLabelAction(Number(props.data[props.row.index].quantity))}
          label={getLabelAction(Number(props.data[props.row.index].quantity))}
          action={props.data[props.row.index].movementType}
        />
      ),
    },
    {
      id: 'qtyToStockUp',
      accessor: 'qtyToStockUp',
      Header: (props) => <CustomHeader tableProps={props} title="Stock on hand" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {Number(props.data[props.row.index].qtyToStockUp)}
        </Typography>
      ),
    },
    {
      id: 'quantity',
      accessor: 'quantity',
      Header: (props) => <CustomHeader tableProps={props} title="Adjustment qty" />,
      width: '10%',
      Cell: ({ ...props }) => <QtyTypography qty={props.data[props.row.index].quantity ?? ''} />,
    },
    {
      id: 'qtyAvailable',
      accessor: 'qtyAvailable',
      Header: (props) => <CustomHeader tableProps={props} title="Available qty" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {props.data[props.row.index].qtyAvailable}
        </Typography>
      ),
    },
    {
      id: 'user',
      accessor: 'episode',
      Header: (props) => <CustomHeader tableProps={props} title="By user" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <UserInfo
          userName={props.data[props.row.index].userName}
          roleName={props.data[props.row.index].roleName}
          movementType={props.data[props.row.index].movementType}
        />
      ),
    },
  ];
};

export const activityLogStockUpAndPickUpColumns = (
  selectLogItem: LogItemDetail,
): ReadonlyArray<Column<ActivityLogRack>> => {
  return [
    {
      id: 'expriryDate',
      width: '10%',
      Header: (props) => <CustomHeader tableProps={props} title="Date modified" />,
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {formatCustomDate(props.data[props.row.index].expriryDate)}
        </Typography>
      ),
    },
    {
      id: 'type',
      accessor: 'type',
      Header: 'Action',
      width: '10%',
      Cell: ({ ...props }) => (
        <React.Fragment>
          {props.data[props.row.index].type && (
            <StatusDefault
              isActive={isActiveLabelAction(Number(props.data[props.row.index].quantity))}
              label={getLabelAction(Number(props.data[props.row.index].quantity))}
              action={props.data[props.row.index].type as InventoryMovementType}
            />
          )}
        </React.Fragment>
      ),
    },
    {
      id: 'bin',
      accessor: 'bin',
      width: '10%',
      Header: (props: any) => <CustomHeader tableProps={props} title="Bin" />,
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {props.data[props.row.index].bin?.name}
        </Typography>
      ),
    },
    {
      id: 'item',
      accessor: 'inventory',
      Header: (props: any) => <CustomHeader tableProps={props} title="Item" />,
      Cell: ({ ...props }) => (
        <React.Fragment>
          {props.data[props.row.index].inventory && (
            <ItemInfoCard
              image={props.data[props.row.index].inventory?.image || ''}
              name={props.data[props.row.index].inventory?.name || ''}
              stockType={props.data[props.row.index].inventory?.stockType}
              isNotNavigatable={true}
            />
          )}
        </React.Fragment>
      ),
    },
    {
      id: 'quantity',
      accessor: 'quantity',
      Header: (props: any) => <CustomHeader tableProps={props} title="Adjustment qty" />,
      width: '10%',
      Cell: ({ ...props }) => <QtyTypography qty={props.data[props.row.index].quantity ?? ''} />,
    },
    {
      id: 'byUser',
      Header: 'By user',
      width: '10%',
      Cell: ({ ...props }) => (
        <UserInfo
          userName={isEmpty(selectLogItem.user) ? '' : getUser(selectLogItem.user)}
          roleName={props.data[props.row.index].roleName}
          movementType={props.data[props.row.index].movementType}
        />
      ),
    },
  ];
};
export const activityLogUpdateRackColumns = (): ReadonlyArray<Column<ActivityLogRack>> => {
  return [
    {
      id: 'updatedAt',
      width: '1%',
      Header: (props) => <CustomHeader tableProps={props} title="Date modified" />,
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {formatCustomDate(props.data[props.row.index].updatedAt)}
        </Typography>
      ),
    },
    {
      id: 'type',
      accessor: 'type',
      Header: 'Action',
      width: '10%',
      Cell: ({ ...props }) => (
        <Typography
          variant="body-5"
          color="neutral-10"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {props.data[props.row.index].action ?? ''}
        </Typography>
      ),
    },

    {
      id: 'detail',
      accessor: 'detail',
      Header: 'Detail',
      Cell: ({ ...props }) => (
        <div
          dangerouslySetInnerHTML={{
            __html: props.data[props.row.index].detail ?? '',
          }}
        />
      ),
    },
    {
      id: 'byUser',
      accessor: 'user',
      Header: 'By user',
      width: '10%',
      Cell: ({ ...props }) => (
        <UserInfo
          userName={
            isEmpty(props.data[props.row.index].user)
              ? ''
              : getUser(props.data[props.row.index].user)
          }
          roleName={props.data[props.row.index].roleName}
          movementType={props.data[props.row.index].movementType}
        />
      ),
    },
  ];
};

export const shortForColumns = (): ReadonlyArray<Column<StockRun>> => {
  return [
    {
      id: 'index',
      accessor: 'index',
      width: 190,
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          title="Stock run index"
          // styleTh={{
          //   paddingRight: 12,
          // }}
        />
      ),
      Cell: ({ ...props }) => (
        <Typography
          variant="body-5"
          color="neutral-10"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {`${props.data[props.row.index].index}`}
        </Typography>
      ),
    },
    {
      id: 'startTime',
      accessor: `startTime`,
      Header: (props) => <CustomHeader tableProps={props} title="Start date" />,
      width: 120,
      Cell: ({ ...props }) =>
        props.data[props.row.index].startTime ? (
          <Typography
            variant="body-5"
            color="neutral-10"
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {formatDate(
              props.data[props.row.index].actualStartTime ?? props.data[props.row.index].startTime,
            )}
          </Typography>
        ) : (
          EmptyTableCell
        ),
    },
    {
      id: 'endTime',
      accessor: `endTime`,
      Header: (props) => <CustomHeader tableProps={props} title="Complete date" />,
      width: 150,
      Cell: ({ ...props }) => (
        <Typography
          variant="body-5"
          color="neutral-10"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(
            props.data[props.row.index].actualEndTime ?? props.data[props.row.index].endTime,
          )}
        </Typography>
      ),
    },
    {
      id: 'createdBy',
      accessor: `createdBy`,
      Header: (props) => <CustomHeader tableProps={props} title="Created by" />,
      width: 130,
      Cell: ({ ...props }) => (
        <div style={{ height: 56, display: 'flex', alignItems: 'center' }}>
          <UserInfo
            userName={props.data[props.row.index].createdBy ?? ''}
            roleName={props.data[props.row.index].roleName ?? ''}
          />
        </div>
      ),
    },
    {
      id: 'statusName',
      accessor: `status`,
      width: 100,
      Header: (props) => <CustomHeader tableProps={props} title="Status" />,
      Cell: ({ ...props }) => (
        <StatusStockRunCard
          status={props.data[props.row.index].status}
          styleLabel={{
            whiteSpace: 'nowrap',
          }}
        />
      ),
    },
    {
      Header: () => <div style={{ width: 32.5 }}></div>,
      id: 'radio',
      width: 22,
      Cell: ({ ..._props }, selected: boolean) => (
        <RadioInput
          className="form-check-input"
          type="radio"
          labelAlign="left"
          checked={selected}
        />
      ),
    },
  ];
};

export const binsAlreadyAssignedColumns: ReadonlyArray<Column<IBinsAlreadyAssignedEntity>> = [
  {
    accessor: 'bins',
    Header: 'Location',
    Cell: ({ value: bin }) => <>{bin?.name}</>,
  },
  {
    accessor: 'items',
    Header: 'Item',
    Cell: ({ value: item }) => <>{item?.name}</>,
  },
  {
    accessor: 'sensors',
    Header: 'Sensor',
    Cell: ({ value: sensor }) => <>{sensor?.skuSub}</>,
  },
  {
    accessor: 'binThings',
    Header: 'Expiry Sensor',
    Cell: ({ value: binThing }) => <>{binThing?.name}</>,
  },
];

const getTooltipTitle = () => (
  <Typography variant="body-4" color="white">
    Go to item detail
  </Typography>
);
